import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import type { OrganizationsList, OrganizationType } from '@openx/types/organization.ts';
import { MAX_ORGANIZATION_LEVEL } from '../../../constants.ts';
import type { OrganizationParentAccount } from '../../../types.ts';

const getParentId = (organization: OrganizationsList): string | undefined =>
  organization.path_to_root?.[organization.path_to_root.length - 1];

/**
 * Flattens organizations into a depth-first ordered tree of parent organization options.
 * Organizations at level deeper than MAX_ORGANIZATION_LEVEL are skipped, as they cannot have sub-organizations.
 */
export const buildParentOrganizationOptions = (organizations: OrganizationsList[]): OrganizationParentAccount[] => {
  const organizationIds = new Set(organizations.map(organization => organization.id));
  const childrenByParentId = new Map<string | undefined, OrganizationsList[]>();

  organizations.forEach(organization => {
    const parentId = getParentId(organization);
    const groupKey = parentId && organizationIds.has(parentId) ? parentId : undefined;
    const siblings = childrenByParentId.get(groupKey) ?? [];
    siblings.push(organization);
    childrenByParentId.set(groupKey, siblings);
  });

  const flattenGroup = (
    parentId: string | undefined,
    depth: number,
    ancestors: OrganizationParentAccount[]
  ): OrganizationParentAccount[] => {
    if (depth > MAX_ORGANIZATION_LEVEL) {
      return [];
    }

    return (childrenByParentId.get(parentId) ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap(organization => {
        const currentOrganization: OrganizationParentAccount = {
          depth,
          feature_flags: organization.feature_flags,
          id: organization.id,
          name: organization.name,
          organization_type: (organization.organization_type as OrganizationType) || undefined,
          parentOrganization: ancestors,
        };
        return [currentOrganization, ...flattenGroup(organization.id, depth + 1, [...ancestors, currentOrganization])];
      });
  };

  return flattenGroup(undefined, 0, []);
};

const isOrganizationNameMatching = (organization: OrganizationParentAccount, searchValue: string): boolean =>
  organization.name.toLowerCase().includes(searchValue);

/**
 * Filters the flattened parent organization tree by name while preserving the hierarchy:
 * - an organization matching the search value is shown together with its path up to the top level organization,
 * - descendants of a matching organization are shown as well.
 * The top level organization (OpenAudience) is common for all organizations, so it is skipped in the paths
 * and shown only when its own name matches the search value.
 */
export const filterParentOrganizationOptions = (
  options: OrganizationParentAccount[],
  { inputValue }: FilterOptionsState<OrganizationParentAccount>
): OrganizationParentAccount[] => {
  const searchValue = inputValue.trim().toLowerCase();

  if (!searchValue) {
    return options;
  }

  const visibleOrganizationIds = new Set<string | undefined>();

  options.forEach(option => {
    const ancestorsBelowTopLevel = (option.parentOrganization ?? []).filter(({ depth }) => depth !== 0);
    const pathToOption = [...ancestorsBelowTopLevel, option];

    if (pathToOption.some(organization => isOrganizationNameMatching(organization, searchValue))) {
      pathToOption.forEach(organization => {
        visibleOrganizationIds.add(organization.id);
      });
    }
  });

  return options.filter(option => visibleOrganizationIds.has(option.id));
};
